import {
  ActivePanelManager,
  ClearButtonBehaviour,
  DateInput,
  NumericInput,
  TextInput,
} from '@infotech-soe/erp-ui-kit';
import {
  CategoryFieldName,
  IDisplayModeEditableFieldMD,
  TDisplayModeEditDirection,
} from '@infotech-soe/metaschema';
import classNames from 'classnames';
import React, {
  KeyboardEvent as ReactKeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { NamedDomain } from '../../../../infrastructure/schemas/storage/NamedDomain';
import { CategoryOpenMode } from '../../../../models/category/category';
import { DatasetGridRow } from '../../../../services/dataset/view-model/DatasetGridRow';
import UniqueId from '../../../../services/uid/UniqueId';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { isDictionaryOpenedByCategoryName } from '../../../../store/selectors/dictionary';
import actions from '../../../../store/slices/actions';
import categoryActions from '../../../../store/slices/category';
import { DictionaryEffectType } from '../../../../store/slices/dictionary';
import LookupBox from '../../LookupBox';
import { CELL_ID_PREFIX, GRID_MODE } from '../constants';

export type CellInputSubmitHandler = (
  ...args:
    | [gridMode: GRID_MODE.EDIT, submitDirection?: TDisplayModeEditDirection]
    | [gridMode: GRID_MODE.VIEW, shouldFocusGrid?: true]
) => void;

interface CellInputProps {
  className?: string;
  columnIndex: number;
  columnName: CategoryFieldName;
  datasetId: UniqueId;
  domain: NamedDomain;
  editingConfig?: IDisplayModeEditableFieldMD;
  row: DatasetGridRow;
  rowIdField: CategoryFieldName;
  style: React.CSSProperties;
  value: null | number | string | Date;
  rows: DatasetGridRow[];
  onSubmit: CellInputSubmitHandler;
  onUpdateRows?: (value: DatasetGridRow[]) => void;
}

export const GRID_CELL_INPUT_CLASS = 'grid__cell-input';

const CellInput = ({
  className,
  columnIndex,
  columnName,
  datasetId,
  domain,
  editingConfig,
  row,
  rowIdField,
  style,
  value,
  onSubmit,
  rows,
  onUpdateRows,
}: CellInputProps): React.JSX.Element => {
  const [editableCellValue, setEditableCellValue] = useState<
    null | undefined | number | string | Date
  >(value);
  const [inputPickerOpen, setInputPickerOpen] = useState<boolean>(false);

  const container = useRef<HTMLDivElement | null>(null);
  const inputElement = useRef<HTMLInputElement | null>(null);

  const dispatch = useAppDispatch();
  const isDictOpen = useAppSelector(state =>
    Boolean(
      editingConfig?.dictionary?.category &&
      isDictionaryOpenedByCategoryName(
        state,
        editingConfig?.dictionary?.category
      )
    )
  );

  const handleCellInputSubmit = useCallback(
    (shouldSubmit: boolean, shouldExitEditMode: boolean, navigate = true) => {
      const { action, submitDirection, required } = editingConfig ?? {};
      if (shouldSubmit) {
        if (
          required &&
          (editableCellValue === null ||
            editableCellValue === undefined ||
            editableCellValue === '')
        ) {
          if (shouldExitEditMode) {
            setEditableCellValue(value);
            onSubmit(GRID_MODE.VIEW);
          }
          return;
        }
        const oldValue = value;
        const hasValueChanged =
          oldValue === null
            ? !!editableCellValue
            : oldValue?.toString() !== editableCellValue?.toString();

        if (hasValueChanged) {
          const updatedRows = [...rows];
          updatedRows[rows.indexOf(row)] = {
            ...row,
            [columnName]:
              editableCellValue instanceof Date
                ? editableCellValue?.toISOString()
                : editableCellValue,
          };
          if (onUpdateRows) {
            onUpdateRows(updatedRows || []);
          }
        }

        if (action && hasValueChanged) {
          dispatch(
            actions.execute({
              datasetId,
              action,
              editedRowContext: { Id: row[rowIdField], Row: row },
              newValue: editableCellValue,
            })
          );
        }
      }
      onSubmit(
        ...(shouldExitEditMode
          ? [GRID_MODE.VIEW]
          : [GRID_MODE.EDIT, navigate ? submitDirection : undefined])
      );
    },
    [
      columnName,
      datasetId,
      dispatch,
      editableCellValue,
      editingConfig,
      row,
      rowIdField,
      value,
      onSubmit,
      rows,
      onUpdateRows,
    ]
  );

  const handleClickOutside: EventListener = useCallback(
    (e: Event) => {
      const target = e.target as HTMLElement;
      const isGridCellClick =
        e.type !== 'contextmenu' &&
        Boolean(target.closest('[data-selectable-column-index]'));

      if (!container.current?.contains(target) && !inputPickerOpen) {
        !isDictOpen &&
          handleCellInputSubmit(true, !isGridCellClick, !isGridCellClick);
      } else {
        inputElement.current?.focus();
      }
    },
    [isDictOpen, inputPickerOpen, handleCellInputSubmit]
  );

  const handleChange = (val: number | null | string | Date) =>
    setEditableCellValue(val);

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === 'Enter' || e.key === 'Tab') {
        handleCellInputSubmit(true, e.key === 'Tab');
      }
    },
    [handleCellInputSubmit]
  );

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      container?.current?.focus();
      handleCellInputSubmit(false, true);
    },
    [handleCellInputSubmit]
  );

  const handleDictionary = useCallback(() => {
    if (!editingConfig?.dictionary) {
      return;
    }
    const { category, displayMode, inParams, outParam } =
      editingConfig.dictionary;
    dispatch(
      categoryActions.openDisplayMode({
        category,
        displayMode,
        ui: {
          mode: CategoryOpenMode.DICTIONARY,
          effect: {
            type: DictionaryEffectType.CALL_BACK,
            callback: (val: any) => {
              setEditableCellValue(val[outParam]);
            },
          },
          multiSelect: false,
        },
        inParams: Object.fromEntries(
          Object.entries(inParams ?? {}).map(([key, val]) => [
            key,
            val.kind === 'control' ? editableCellValue : val.val,
          ])
        ),
      })
    );
  }, [dispatch, editableCellValue, editingConfig?.dictionary]);

  const handleInputPickerOpen = (open: boolean) => setInputPickerOpen(open);

  useEffect(() => {
    if (inputElement.current) {
      inputElement.current.focus();
      const timeout = setTimeout(() => inputElement.current?.select());
      return () => clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('contextmenu', handleClickOutside, true);
    document.addEventListener('keydown', handleEscapeKeyDown, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('contextmenu', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscapeKeyDown, true);
    };
  }, [handleClickOutside, handleEscapeKeyDown]);

  return (
    <div
      ref={container}
      style={style}
      className={classNames(GRID_CELL_INPUT_CLASS, className)}
    >
      {domain.type === 'number' ? (
        <NumericInput
          ref={inputElement}
          className={ActivePanelManager.helpers.ignore}
          value={editableCellValue as number | null}
          precision={domain.precision}
          required={editingConfig?.required}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      ) : domain.type === 'object' && domain.class === 'Date' ? (
        <DateInput
          ref={inputElement}
          className={ActivePanelManager.helpers.ignore}
          value={editableCellValue as Date | null}
          required={editingConfig?.required}
          withClearButton={ClearButtonBehaviour.NONE}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPickerOpen={handleInputPickerOpen}
        />
      ) : editingConfig?.dictionary ? (
        <LookupBox
          ref={inputElement}
          className={ActivePanelManager.helpers.ignore}
          id={`${CELL_ID_PREFIX}-${row[rowIdField]}-${columnIndex}`}
          value={editableCellValue as string | null}
          required={editingConfig?.required}
          readOnly={editingConfig.dictionary.readonly}
          canClearOnReadOnly={editingConfig.dictionary.canClearOnReadOnly}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onButtonClick={handleDictionary}
        />
      ) : (
        <TextInput
          ref={inputElement}
          className={ActivePanelManager.helpers.ignore}
          id={`${CELL_ID_PREFIX}-${row[rowIdField]}-${columnIndex}`}
          value={editableCellValue as string | null}
          required={editingConfig?.required}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      )}
    </div>
  );
};

export default memo(CellInput);
